// Maps a fully priced roller shade to its exact Shopify variant and talks to the Shopify AJAX cart.
// Needs NoDrillTieredPricePreview and NoDrillCartVariants. Never invents a price, id or cart.
(function(root, make) {
  if (typeof module === 'object' && module.exports) {
    module.exports = make(() => require('./no-drill-cart-variants.js'), () => require('./no-drill-tiered-price-preview.js'));
  } else {
    root.NoDrillCartClient = make(() => root.NoDrillCartVariants, () => root.NoDrillTieredPricePreview);
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function(getVariants, getPricing) {
  'use strict';

  class CartError extends Error {
    constructor(code, message, details = {}) {
      super(message);
      this.name = 'CartError';
      this.code = code;
      this.status = details.status ?? null;
      this.item = details.item ?? null;
    }
  }

  const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const isId = value => Number.isSafeInteger(value) && value > 0;
  const denied = reason => ({ valid: false, id: null, totalCents: null, sku: null, reason });

  // Same mount rules as the price table: only the two launch mounts have Shopify variants.
  function mountFor(options) {
    if (options.installation_method === 'no-drill' && options.mount_location === 'inside'
      && options.headrail_style === 'no-drill-square-cassette') return 'inside';
    if (options.installation_method === 'standard-bracket' && options.mount_location === 'outside'
      && options.headrail_style === 'standard-bracket-cassette') return 'outside';
    return null;
  }

  let cached = null;
  function table() {
    const data = getVariants();
    if (!data || !Array.isArray(data.products) || !Array.isArray(data.fields)) return null;
    if (cached && cached.data === data) return cached;
    const at = Object.fromEntries(data.fields.map((field, index) => [field, index]));
    const byKey = new Map();
    const byId = new Map();
    for (const product of data.products) {
      if (typeof product?.family !== 'string' || !/^NDR-V2-RL-(BO|LF|SO)-\d{3}$/.test(product.family)) return null;
      for (const row of product.variants) {
        const variant = {
          id: row[at.id], cents: row[at.cents], sku: row[at.sku],
          // Per-collection price products (NDR-D-094): one hidden product per collection and mount.
          key: [product.mount, product.family, row[at.widthBand], row[at.heightBand], row[at.control], row[at.chainMaterial]].join('|'),
        };
        byKey.set(variant.key, variant);
        byId.set(variant.id, variant);
      }
    }
    const addonsBySku = new Map();
    const addonsById = new Map();
    let addonsValid = Array.isArray(data.addons) && data.addons.length > 0;
    for (const entry of Array.isArray(data.addons) ? data.addons : []) {
      const role = /^NDR-SC-[LU]-H(0[1-9]|1[0-2])$/.test(entry?.sku) ? 'side_channel'
        : /^NDR-RM-(01|06|16)CH$/.test(entry?.sku) ? 'remote' : null;
      if (!role || !isId(entry.id) || !Number.isSafeInteger(entry.cents) || entry.cents <= 0
          || byId.has(entry.id) || addonsById.has(entry.id) || addonsBySku.has(entry.sku)) {
        addonsValid = false;
        continue;
      }
      const variant = { ...entry, role };
      addonsBySku.set(entry.sku, variant);
      addonsById.set(entry.id, variant);
    }
    cached = { data, byKey, byId, addonsBySku, addonsById, addonsValid };
    return cached;
  }

  function resolve(configuration, priceResult) {
    if (!isPlainObject(configuration) || !isPlainObject(configuration.options)) return denied('configuration-missing');
    if (!isPlainObject(priceResult)) return denied('price-missing');
    if (priceResult.valid !== true) return denied('price-pending');
    if (priceResult.starting !== false) return denied(priceResult.starting === true ? 'starting-price' : 'price-mismatch');
    const pricing = getPricing();
    const variants = table();
    if (!pricing || typeof pricing.priceConfiguration !== 'function' || !variants) return denied('variants-unavailable');

    // Re-price the configuration; a quote from another or edited selection must not reach the cart.
    const fresh = pricing.priceConfiguration(configuration);
    if (!fresh.valid || fresh.starting || !Number.isSafeInteger(fresh.totalCents)) return denied('price-pending');
    for (const field of ['valid', 'starting', 'totalCents', 'currency', 'tierId', 'familyId']) {
      if (priceResult[field] !== fresh[field]) return denied('price-mismatch');
    }

    const { widthBands, heightBands, currency } = variants.data;
    const mount = mountFor(configuration.options);
    const widthBand = widthBands.find(edge => configuration.width <= edge);
    const heightBand = heightBands.find(edge => configuration.height <= edge);
    const control = configuration.control;
    const chainMaterial = control === 'pull_chain' ? configuration.options.chain_material : null;
    if (fresh.currency !== currency || !mount || widthBand === undefined || heightBand === undefined) return denied('variant-missing');
    if (typeof fresh.familyId !== 'string' || fresh.familyId !== configuration.options.fabric_family) return denied('variant-missing');
    const variant = variants.byKey.get([mount, fresh.familyId, widthBand, heightBand, control, chainMaterial].join('|'));
    if (!variant) return denied('variant-missing');
    if (variant.cents !== fresh.totalCents) return denied('variant-price-mismatch');
    return { valid: true, id: variant.id, totalCents: variant.cents, sku: variant.sku, reason: null };
  }

  const remoteSkus = { one_channel: 'NDR-RM-01CH', six_channel: 'NDR-RM-06CH', sixteen_channel: 'NDR-RM-16CH' };

  function resolveAddon(sku, price, quantity, role) {
    const variants = table();
    const variant = variants?.addonsValid && variants.addonsBySku.get(sku);
    if (!variant || !price || price.valid !== true) return denied('addons-unavailable');
    const unitCents = role === 'remote' ? price.unitCents : price.totalCents;
    if (!Number.isSafeInteger(unitCents) || unitCents <= 0 || variant.cents !== unitCents
      || price.totalCents !== unitCents * quantity) return denied('addon-price-mismatch');
    return { valid: true, id: variant.id, sku, role, quantity, unitCents, totalCents: unitCents * quantity, reason: null };
  }

  function resolveRemote(type, quantity) {
    if (!Object.prototype.hasOwnProperty.call(remoteSkus, type) || !Number.isInteger(quantity) || quantity < 1 || quantity > 5) {
      return denied('remote-invalid');
    }
    const pricing = getPricing();
    if (typeof pricing?.priceRemote !== 'function') return denied('addons-unavailable');
    return resolveAddon(remoteSkus[type], pricing.priceRemote({ type, quantity }), quantity, 'remote');
  }

  function resolveItems(configuration, priceResult) {
    const selectedAddon = ['l_channel', 'u_channel'].includes(configuration?.sideChannel)
      || (configuration?.control === 'motorized' && Object.prototype.hasOwnProperty.call(remoteSkus, configuration.remoteType));
    if (selectedAddon && !table()?.addonsValid) return denied('addons-unavailable');
    const shade = resolve(configuration, priceResult);
    if (!shade.valid) return shade;
    const items = [{ ...shade, role: 'shade', quantity: 1, unitCents: shade.totalCents }];
    const sideChannel = configuration.sideChannel ?? 'none';
    if (sideChannel !== 'none') {
      if (!['l_channel', 'u_channel'].includes(sideChannel)
        || !/^NDR-V2-RL-BO-\d{3}$/.test(configuration.options.fabric_family)) return denied('side-channel-invalid');
      const pricing = getPricing();
      if (typeof pricing?.priceSideChannel !== 'function') return denied('addons-unavailable');
      // Side channels keep their own 12 height bands, separate from the shade's bands.
      const index = (pricing.sideChannelBands || []).findIndex(edge => configuration.height <= edge);
      if (index < 0) return denied('side-channel-invalid');
      const sku = `NDR-SC-${sideChannel === 'l_channel' ? 'L' : 'U'}-H${String(index + 1).padStart(2, '0')}`;
      const channel = resolveAddon(sku, pricing.priceSideChannel({ height: configuration.height, sideChannel,
        fabricFamily: configuration.options.fabric_family }), 1, 'side_channel');
      if (!channel.valid) return channel;
      items.push(channel);
    }
    if (configuration.control === 'motorized') {
      if (configuration.remoteType === 'none') {
        if (configuration.compatibleRemoteAcknowledgement !== true) return denied('remote-acknowledgement-required');
      } else {
        const remote = resolveRemote(configuration.remoteType, configuration.remoteQuantity);
        if (!remote.valid) return remote;
        items.push(remote);
      }
    } else if (configuration.remoteType && configuration.remoteType !== 'none') {
      return denied('remote-invalid');
    }
    return { ...shade, items, totalCents: items.reduce((total, item) => total + item.totalCents, 0) };
  }

  function normalizeRoot(value) {
    if (value === undefined || value === null || value === '') return '/';
    if (typeof value !== 'string' || !/^\/(?!\/)[^?#\\\s]*$/.test(value)) {
      throw new TypeError('Cart root must be a same-origin path such as Shopify.routes.root.');
    }
    return value.endsWith('/') ? value : `${value}/`;
  }

  function checkItem(item) {
    if (!isPlainObject(item)) return 'The cart item is missing.';
    const extra = Object.keys(item).filter(key => !['id', 'quantity', 'properties'].includes(key));
    if (extra.length) return `We could not add this window with the details you chose.`;
    if (!isId(item.id)) return 'We could not add this shade to your cart.';
    if (item.quantity !== 1) return 'Each window you set up is added with a count of 1.';
    if (!isPlainObject(item.properties) || !Object.values(item.properties).every(value => typeof value === 'string')) {
      return 'We could not add this window with the details you chose.';
    }
    return null;
  }

  function create({ fetch, root } = {}) {
    if (typeof fetch !== 'function') throw new TypeError('A fetch function is required.');
    const base = normalizeRoot(root);

    async function request(path, init) {
      let response;
      try {
        response = await fetch(`${base}${path}`, { ...init, credentials: 'same-origin', headers: { Accept: 'application/json', ...init.headers } });
      } catch (failure) {
        throw new CartError('network-error', 'Could not reach the cart. Check your connection.', { item: null });
      }
      const text = await response.text().catch(() => '');
      let body = null;
      try { body = text ? JSON.parse(text) : null; } catch (_) { body = null; }
      if (!response.ok) {
        const detail = isPlainObject(body) ? body.description || body.message : null;
        throw new CartError('http-error', typeof detail === 'string' && detail ? detail : `We could not update your cart.`, { status: response.status });
      }
      if (!isPlainObject(body)) throw new CartError('invalid-response', 'We could not confirm your cart details.', { status: response.status });
      return body;
    }

    const isCart = body => Array.isArray(body.items) && Number.isSafeInteger(body.item_count);

    async function get() {
      const cart = await request('cart.js', { method: 'GET' });
      if (!isCart(cart)) throw new CartError('invalid-response', 'We could not confirm your cart details.');
      return cart;
    }

    // Single POST, never retried: a lost response may still have added the line.
    async function add(item, expectedCents) {
      const problem = checkItem(item);
      if (problem) throw new CartError('invalid-item', problem);
      const variant = table()?.byId.get(item.id);
      if (!variant) throw new CartError('unknown-variant', 'You cannot add this shade with these choices to your cart.');
      if (expectedCents !== variant.cents) throw new CartError('price-mismatch', 'The cart price does not match the price shown. Check your cart before you try again.');
      const body = await request('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: item.id, quantity: 1, properties: item.properties }] }),
      });
      if (!Array.isArray(body.items) || body.items.length !== 1 || !isPlainObject(body.items[0])) {
        throw new CartError('invalid-response', 'The cart did not confirm the added window.');
      }
      const line = body.items[0];
      if (line.variant_id !== item.id) throw new CartError('variant-mismatch', 'The cart added a different item.', { item: line });
      if (line.original_price !== expectedCents) throw new CartError('cart-price-mismatch', 'The cart price does not match the price shown.', { item: line });
      if (line.quantity !== 1) throw new CartError('quantity-mismatch', 'This window was merged with an item in your cart. Please check the counts.', { item: line });
      return line;
    }

    function validateItems(items, expectedUnitCents) {
      if (!Array.isArray(items) || !items.length || items.length > 3) throw new CartError('invalid-item', 'We could not confirm the items you chose. Check your choices.');
      if (!Array.isArray(expectedUnitCents) || expectedUnitCents.length !== items.length) throw new CartError('price-mismatch', 'We could not confirm the price for each item you chose.');
      const configurationId = items[0]?.properties?._configuration_id;
      const roles = new Set();
      const variants = table();
      items.forEach((item, index) => {
        // The single-window shape is retained; only remote quantities may be greater than one.
        const problem = checkItem(isPlainObject(item) ? { ...item, quantity: 1 } : item);
        if (problem) throw new CartError('invalid-item', problem);
        const role = item.properties._ndr_line_role;
        const remote = role === 'remote';
        if (!['shade', 'side_channel', 'remote'].includes(role) || roles.has(role)
          || typeof configurationId !== 'string' || !configurationId.trim()
          || item.properties._configuration_id !== configurationId || item.properties._ndr_cart_version !== '2'
          || (remote ? !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 5 : item.quantity !== 1)) {
          throw new CartError('invalid-item', 'Some item details or counts do not match your choices. Check your cart before you try again.');
        }
        roles.add(role);
        const variant = role === 'shade' ? variants?.byId.get(item.id)
          : variants?.addonsValid && variants.addonsById.get(item.id);
        if (!variant || (role !== 'shade' && variant.role !== role)) throw new CartError('unknown-variant', 'You cannot add this choice to your cart.');
        if (!Number.isSafeInteger(expectedUnitCents[index]) || expectedUnitCents[index] !== variant.cents) {
          throw new CartError('price-mismatch', 'An item you chose has a price that does not match the one shown. Check your cart before you try again.');
        }
      });
      if ((!roles.has('shade') && !(items.length === 1 && roles.has('remote') && configurationId === 'order'))
        || (roles.has('shade') && configurationId === 'order')) throw new CartError('invalid-item', 'We could not match these items to your window. Check your choices.');
    }

    function confirmItems(items, expectedUnitCents, lines) {
      if (!Array.isArray(lines) || lines.length !== items.length) throw new CartError('invalid-response', 'We could not confirm all the items you chose. Check your cart before you try again.');
      items.forEach((item, index) => {
        const matches = lines.filter(line => line && line.variant_id === item.id && line.properties?._ndr_line_role === item.properties._ndr_line_role);
        if (matches.length !== 1) throw new CartError('variant-mismatch', 'The items in your cart do not match what you chose. Check your cart before you try again.');
        const line = matches[0];
        if (line.original_price !== expectedUnitCents[index]) throw new CartError('cart-price-mismatch', 'The cart price does not match the price shown.', { item: line });
        if (line.quantity !== item.quantity) throw new CartError('quantity-mismatch', 'The count in your cart does not match what you chose.', { item: line });
        if (Object.keys(line.properties).length !== Object.keys(item.properties).length
          || Object.entries(item.properties).some(([key, value]) => line.properties[key] !== value)) {
          throw new CartError('properties-mismatch', 'Your choices changed in the cart. Check your cart before you try again.', { item: line });
        }
      });
      return lines;
    }

    // Exactly one POST for the whole selection. No automatic retry after an ambiguous response.
    async function addItems(items, expectedUnitCents) {
      validateItems(items, expectedUnitCents);
      const body = await request('cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      return confirmItems(items, expectedUnitCents, body.items);
    }

    function reconcile(items, expectedUnitCents, cart) {
      validateItems(items, expectedUnitCents);
      if (!isPlainObject(cart) || !Array.isArray(cart.items)) throw new CartError('invalid-response', 'Could not read the current cart.');
      const lines = cart.items.filter(line => line?.properties?._configuration_id === items[0].properties._configuration_id);
      if (!lines.length) return false;
      confirmItems(items, expectedUnitCents, lines);
      return true;
    }

    async function remove(key) {
      if (typeof key !== 'string' || !key) throw new CartError('invalid-item', 'A cart line key is required.');
      const cart = await request('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      if (!isCart(cart) || cart.items.some(line => line && line.key === key)) {
        throw new CartError('invalid-response', 'The cart did not confirm the removal.');
      }
      return cart;
    }

    return Object.freeze({ add, addItems, reconcile, get, remove });
  }

  return Object.freeze({ resolve, resolveItems, resolveRemote, create, CartError });
});
